/**
 * ConditionGroup evaluator — recursive AND/OR.
 *
 * VERBATIM parity port of the canonical evaluators:
 *   - main-frontend `src/lib/utils/evaluate-condition.ts`
 *   - main-consumer `src/lib/workflow/condition/evaluator.ts`
 *   - website-builder-backend `src/lib/workflow/condition/evaluator.ts`
 *   - main-backend  `org/nikita/workflow/condition/ConditionEvaluator.java`
 *
 * Used here to evaluate the additional-field-definition conditions
 * (visibility / editability / per-option) live against the guest's current
 * registration-form values. The website-builder-backend re-evaluates the same
 * ConditionGroup at submit time, so this MUST stay aligned — do not "improve"
 * the operator semantics here without changing all the twins.
 *
 * Empty / null group → returns `true` (fire-on-no-conditions semantics).
 *
 * Two rules ported from the Worker (audit 2026-09-23, 4c / W1j), pinned by the
 * shared fixture `src/lib/__fixtures__/condition-eval-parity.json`:
 *   - a SUB-group carrying no filter anywhere beneath it gets no vote
 *     (`groupHasFilters`); under OR an empty `{}` beside a real group used to
 *     answer TRUE and make every guest match.
 *   - `=` / `!=` / `in` / `not_in` compare ISO datetimes canonically: the JS
 *     `toISOString()` form `…T00:00:00.000Z` equals Java's `Instant.toString()`
 *     `…T00:00:00Z`.
 *
 * NOTE: this is distinct from the builder's node-level `ParentFilter` /
 * `evaluateVisibilityFilter` (shared.tsx), which gates canvas components. This
 * evaluator handles the field-definition ConditionGroup wire shape only.
 */

export interface ConditionFilter {
  key: string;
  operator: string;
  value: string | string[];
}

export interface ConditionGroup {
  operator: "AND" | "OR";
  filters?: ConditionFilter[];
  groups?: ConditionGroup[];
}

/**
 * Does this group carry a filter anywhere beneath it? A group whose only
 * content is more empty groups is not a rule, however deep it nests; `null`,
 * `undefined` and non-objects are not rules either. Same shape as the
 * Worker's `groupHasFilters` (`workflow/condition/evaluator.ts`).
 */
export function groupHasFilters(conds: unknown): boolean {
  if (conds === null || conds === undefined || typeof conds !== "object") {
    return false;
  }
  const filters = (conds as { filters?: unknown[] }).filters;
  if (Array.isArray(filters) && filters.length > 0) return true;
  const groups = (conds as { groups?: unknown[] }).groups;
  if (!Array.isArray(groups)) return false;
  return groups.some((g) => groupHasFilters(g));
}

/**
 * `2026-01-01T00:00:00.000Z` (JS `toISOString()`) and `2026-01-01T00:00:00Z`
 * (Java `Instant.toString()`) are the same instant; a `datetime-local` field
 * is submitted in the former, an operand authored from a Java-rendered value
 * arrives in the latter. Applied to BOTH sides of the equality operators.
 *
 * Re-audit 2026-09-23 (F-2/P3): the fold is Java's exact rule — ONLY the
 * 24-char `YYYY-MM-DDTHH:MM:SS.000Z` shape loses its millis. A bare
 * `\.000Z$` strip also folded `v1.000Z` and unpadded `2026-1-1T0:0:0.000Z`,
 * which the server does not, so preview and submit disagreed on those.
 * Pinned by the shared fixture (`condition-eval-parity.json`).
 */
const ISO_ZERO_MILLIS = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.000Z$/;

function canonicalIso(s: string): string {
  return ISO_ZERO_MILLIS.test(s) ? s.slice(0, 19) + "Z" : s;
}

export function evaluateConditionGroup(
  conditionGroup: ConditionGroup | null | undefined,
  data: Record<string, unknown> | null | undefined,
  previousData?: Record<string, unknown> | null,
): boolean {
  if (
    conditionGroup === null ||
    conditionGroup === undefined ||
    Object.keys(conditionGroup).length === 0
  ) {
    return true;
  }

  const operator = conditionGroup.operator ?? "AND";
  const filters = conditionGroup.filters;
  const groups = conditionGroup.groups;

  let hasAny = false;

  if (Array.isArray(filters)) {
    for (const filter of filters) {
      hasAny = true;
      const result = evaluateFilter(filter, data, previousData ?? null);
      if (operator === "OR" && result) return true;
      if (operator === "AND" && !result) return false;
    }
  }

  if (Array.isArray(groups)) {
    for (const subgroup of groups) {
      // A subgroup with no filter anywhere beneath it is not a rule and must
      // not get a vote: under OR the fire-on-no-conditions `true` of an empty
      // `{}` beside a real group made EVERY guest match; under AND it was a
      // free pass. The top-level "no conditions at all" answer is untouched —
      // `hasAny` below still decides that.
      if (!groupHasFilters(subgroup)) continue;
      hasAny = true;
      const result = evaluateConditionGroup(subgroup, data, previousData ?? null);
      if (operator === "OR" && result) return true;
      if (operator === "AND" && !result) return false;
    }
  }

  if (!hasAny) return true;
  return operator === "AND";
}

function evaluateFilter(
  filter: ConditionFilter,
  data: Record<string, unknown> | null | undefined,
  previousData: Record<string, unknown> | null,
): boolean {
  const key = filter.key;
  const op = filter.operator;
  const filterValue = filter.value;

  if (key === null || key === undefined || op === null || op === undefined) {
    return false;
  }

  const actualValue = resolveValue(key, data);
  const actual =
    actualValue !== null && actualValue !== undefined ? String(actualValue) : "";
  const expected =
    filterValue !== null && filterValue !== undefined
      ? Array.isArray(filterValue)
        ? ""
        : String(filterValue)
      : "";

  switch (op) {
    case "=":
      return canonicalIso(actual) === canonicalIso(expected);
    case "!=":
      return canonicalIso(actual) !== canonicalIso(expected);
    case "contains":
      if (Array.isArray(actualValue)) {
        return (actualValue as unknown[]).map(String).includes(expected);
      }
      // "contains" is a question about TEXT. `actual` is String(actualValue), so
      // without this guard a boolean/number/date was rendered and then searched
      // — TRUE here where the server's Mongo twin ($regex) says FALSE, and this
      // file's header demands byte-parity with it.
      if (!isTextual(actualValue)) return false;
      return actual.toLowerCase().includes(expected.toLowerCase());
    case "not_contains":
      if (Array.isArray(actualValue)) {
        return !(actualValue as unknown[]).map(String).includes(expected);
      }
      // Mongo's negated regex on a non-string field matches every document.
      if (!isTextual(actualValue)) return true;
      return !actual.toLowerCase().includes(expected.toLowerCase());
    case "is_empty":
      return (
        actualValue === null ||
        actualValue === undefined ||
        actual.length === 0 ||
        (Array.isArray(actualValue) && (actualValue as unknown[]).length === 0)
      );
    case "is_not_empty":
      return (
        actualValue !== null &&
        actualValue !== undefined &&
        actual.length !== 0 &&
        !(Array.isArray(actualValue) && (actualValue as unknown[]).length === 0)
      );
    // An ordered comparison against a LIST is not a question with an answer;
    // this string-compared String(array), i.e. the literal "A,B".
    case ">":
      if (Array.isArray(actualValue)) return false;
      return compareValues(actual, expected) > 0;
    case "<":
      if (Array.isArray(actualValue)) return false;
      return compareValues(actual, expected) < 0;
    case ">=":
      if (Array.isArray(actualValue)) return false;
      return compareValues(actual, expected) >= 0;
    case "<=":
      if (Array.isArray(actualValue)) return false;
      return compareValues(actual, expected) <= 0;
    case "changed_to": {
      if (previousData === null) return false;
      const oldRaw = resolveValue(key, previousData);
      const oldVal = oldRaw !== null && oldRaw !== undefined ? String(oldRaw) : "";
      return oldVal !== expected && actual === expected;
    }
    case "changed_from": {
      if (previousData === null) return false;
      const oldRaw = resolveValue(key, previousData);
      const oldVal = oldRaw !== null && oldRaw !== undefined ? String(oldRaw) : "";
      return oldVal === expected && actual !== expected;
    }
    case "in":
    case "not_in": {
      const arr = (
        Array.isArray(filterValue)
          ? (filterValue as unknown[]).map(String)
          : filterValue === null || filterValue === undefined
            ? []
            : [String(filterValue)]
      ).map(canonicalIso);
      const member = Array.isArray(actualValue)
        ? (actualValue as unknown[])
            .map((a) => canonicalIso(String(a)))
            .some((a) => arr.includes(a))
        : arr.includes(canonicalIso(actual));
      return op === "in" ? arr.length > 0 && member : arr.length === 0 || !member;
    }
    default:
      return false;
  }
}

/**
 * Resolve a key against the data object. Lookup order mirrors the server
 * evaluators exactly:
 *   1. Direct field lookup.
 *   2. Dotted path (`additionalFields.vipStatus`) — split on first dot.
 *   3. `additionalFields.*` fallback for un-prefixed keys.
 */
function resolveValue(
  key: string,
  data: Record<string, unknown> | null | undefined,
): unknown {
  if (data === null || data === undefined || key === null || key === undefined) {
    return null;
  }

  if (Object.prototype.hasOwnProperty.call(data, key)) return data[key];

  const dotIdx = key.indexOf(".");
  if (dotIdx > 0) {
    const head = key.substring(0, dotIdx);
    const tail = key.substring(dotIdx + 1);
    const nested = data[head];
    if (nested !== null && typeof nested === "object" && !Array.isArray(nested)) {
      return (nested as Record<string, unknown>)[tail] ?? null;
    }
  }

  const additionalFields = data["additionalFields"];
  if (
    additionalFields !== null &&
    typeof additionalFields === "object" &&
    !Array.isArray(additionalFields)
  ) {
    const v = (additionalFields as Record<string, unknown>)[key];
    if (v !== null && v !== undefined) return v;
  }

  return null;
}

function compareValues(a: string, b: string): number {
  if (a.length === 0 && b.length === 0) return 0;
  if (a.length === 0) return -1;
  if (b.length === 0) return 1;

  const numA = parseStrictNumber(a);
  const numB = parseStrictNumber(b);
  if (numA !== null && numB !== null) {
    if (numA < numB) return -1;
    if (numA > numB) return 1;
    return 0;
  }

  const dtA = parseDateTime(a);
  const dtB = parseDateTime(b);
  if (dtA !== null && dtB !== null) {
    if (dtA < dtB) return -1;
    if (dtA > dtB) return 1;
    return 0;
  }

  return a < b ? -1 : a > b ? 1 : 0;
}

function parseStrictNumber(s: string): number | null {
  if (!/^-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function parseDateTime(value: string): number | null {
  // Z OR explicit ±HH:MM offset — matches the Java/Worker evaluators
  // (main-consumer + website-builder-backend). A Z-only regex here would make
  // an offset datetime string-compare on the client while the enforcing
  // backend date-compares it → the form could show a field/option the submit
  // then strips/rejects.
  if (!/T.*(Z|[+-]\d{2}:\d{2})$/.test(value)) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Is this value TEXT, i.e. can "contains" mean anything about it?
 *
 * An ALLOWLIST — a new stored type defaults to "not text" and is refused rather
 * than rendered with String() and searched.
 *
 * ⚠ NOT byte-parity with Java, despite what this comment used to claim.
 *
 * Java guards `contains`/`not_contains` with TWO tests —
 * `!isTextual(value) || FilterParser.isNonTextualGuestKey(key)` — and its own
 * javadoc says the VALUE test alone is not enough: the payload builder
 * stringifies Boolean, ObjectId, Enum and Instant BEFORE evaluation, so
 * `checkedIn` arrives as the String "true" and passes any value-shaped test.
 * Java's `isTextual` also accepts Enum and Character, which this does not.
 *
 * This copy has the value test only. For a non-string BUILT-IN key
 * (`checkedIn`, `archived`, `emailOptIn`, `guestId`, `mainGuest`, …) `contains`
 * answers TRUE here and FALSE in main-backend, and `not_contains` inverts.
 * Closing it needs the key type table, which is its own change across every
 * copy at once.
 */
function isTextual(v: unknown): boolean {
  return typeof v === "string";
}
